import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import {
  authService,
  AuthResult,
  LoginCredentials,
  MfaMethod,
  User,
  Workspace
} from '../services/authService'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  customerId: number | null
  capabilities: string[]
  workspaces: Workspace[]
  activeCustomerId: number | null
  isPlatformAdmin: boolean
  login: (credentials: LoginCredentials) => Promise<AuthResult>
  sendMfaEmail: (challengeToken: string) => Promise<{ success: boolean; error?: string }>
  sendMfaEnrollmentEmail: (challengeToken: string) => Promise<{ success: boolean; error?: string }>
  verifyMfa: (challengeToken: string, method: MfaMethod, code: string) => Promise<AuthResult>
  confirmMfaEnrollment: (challengeToken: string, method: Exclude<MfaMethod, 'recovery'>, code: string) => Promise<AuthResult>
  acceptInvitation: (token: string, password: string) => Promise<AuthResult>
  switchWorkspace: (customerId: number | null) => Promise<AuthResult>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  can: (capability: string) => boolean
  isAdmin: () => boolean
  canWrite: () => boolean
  canManageUsers: () => boolean
  getRoleDisplayName: () => string
  getRoleColor: () => string
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (!authService.isAuthenticated()) return setUser(null)
        const currentUser = await authService.getCurrentUser()
        if (currentUser) setUser(currentUser)
        else {
          await authService.logout()
          setUser(null)
        }
      } finally {
        setIsLoading(false)
      }
    }
    initializeAuth()
  }, [])

  const applyResult = (result: AuthResult): AuthResult => {
    if (result.status === 'authenticated') setUser(result.user)
    return result
  }

  const login = async (credentials: LoginCredentials) => {
    setIsLoading(true)
    try {
      return applyResult(await authService.login(credentials))
    } finally {
      setIsLoading(false)
    }
  }

  const verifyMfa = async (challengeToken: string, method: MfaMethod, code: string) =>
    applyResult(await authService.verifyMfa(challengeToken, method, code))

  const confirmMfaEnrollment = async (
    challengeToken: string,
    method: Exclude<MfaMethod, 'recovery'>,
    code: string
  ) => applyResult(await authService.confirmMfaEnrollment(challengeToken, method, code))

  const acceptInvitation = async (token: string, password: string) =>
    applyResult(await authService.acceptInvitation(token, password))

  const switchWorkspace = async (customerId: number | null) => {
    setIsLoading(true)
    try {
      return applyResult(await authService.switchWorkspace(customerId))
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    setIsLoading(true)
    try {
      await authService.logout()
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  const refreshUser = async () => {
    const currentUser = await authService.getCurrentUser()
    setUser(currentUser)
  }

  const can = (capability: string): boolean => {
    if (!user) return false
    const required = user.accountType === 'CUSTOMER' && capability === 'users.manage'
      ? 'customer.users.manage'
      : capability
    return user.isPlatformAdmin || user.capabilities.includes('*') || user.capabilities.includes(required)
  }

  const isAdmin = () => Boolean(user?.isPlatformAdmin || can('users.manage') || can('settings.manage'))
  const canWrite = () => Boolean(user && (
    user.capabilities.includes('*') ||
    user.capabilities.some(capability => capability.endsWith('.manage'))
  ))
  const canManageUsers = () => can('users.manage')
  const getRoleDisplayName = () => {
    if (user?.isPlatformAdmin) return 'Platform administrator'
    if (user?.roleTemplate?.displayName) return user.roleTemplate.displayName
    if (user?.role === 'ADMIN') return 'Administrator'
    if (user?.role === 'OPERATOR') return 'Operator'
    if (user?.role === 'VIEWER') return 'Viewer'
    return 'Guest'
  }
  const getRoleColor = () => {
    if (user?.isPlatformAdmin) return 'text-violet-600'
    if (user?.accountType === 'FLEET_TEAM') return 'text-indigo-600'
    if (user?.role === 'ADMIN') return 'text-red-600'
    if (user?.role === 'OPERATOR') return 'text-blue-600'
    if (user?.role === 'VIEWER') return 'text-green-600'
    return 'text-gray-600'
  }

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: Boolean(user && authService.isAuthenticated()),
      isLoading,
      customerId: user?.activeCustomerId ?? user?.customerId ?? null,
      capabilities: user?.capabilities ?? [],
      workspaces: user?.workspaces ?? [],
      activeCustomerId: user?.activeCustomerId ?? null,
      isPlatformAdmin: user?.isPlatformAdmin ?? false,
      login,
      sendMfaEmail: authService.sendMfaEmail.bind(authService),
      sendMfaEnrollmentEmail: authService.sendMfaEnrollmentEmail.bind(authService),
      verifyMfa,
      confirmMfaEnrollment,
      acceptInvitation,
      switchWorkspace,
      logout,
      refreshUser,
      can,
      isAdmin,
      canWrite,
      canManageUsers,
      getRoleDisplayName,
      getRoleColor
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within an AuthProvider')
  return context
}

export default AuthContext
